import React, {useState} from "react";
import {useLocation, useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";

import FlexContainer from "connectui/component/layout/FlexContainer";
import SvgIcon from "connectui/component/display/SvgIcon";
import ProfilePicture from "connectui/component/display/ProfilePicture";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import Tooltip from "connectui/component/display/Tooltip";
import Text from "connectui/component/display/Text";

const routes = [
  {id: "Dashboard", icon: ICONS["dashboard-bar"], route: "/admin/dashboard"},
  {id: "Listings", icon: ICONS["check-in"], route: "/admin/listings"},
  {id: "Messages", icon: ICONS["inbox-filled"], route: "/admin/messages"},
  {id: "CRM", icon: ICONS.users, route: "/admin/guests"},
  {id: "Business Settings", icon: ICONS.briefcase, route: "/admin/settings"},
];

const mobileRoutes = [
  {id: "Dashboard", icon: ICONS["check-in"], route: "/admin/home"},
  {id: "Messages", icon: ICONS.messages, route: "/admin/messages"},
  {id: "Reports", icon: ICONS["bar-chart"], route: "/admin/dashboard"},
];

const useStyles = (theme) =>
  buildStyles({
    btnIcon: {
      width: "48px",
      height: "48px",
      border: "none",
      padding: theme.spacing(3.5),
      backgroundColor: "transparent",
    },
    mobileBtnIcon: {
      width: "40px",
      height: "40px",
      border: "none",
      padding: theme.spacing(1.5),
      backgroundColor: "transparent",
    },
    icon: {
      color: theme.colors.gray600,
      "&:hover": {
        color: theme.colors.primary,
      },
    },
    activeIcon: {
      color: theme.colors.primary,
      "&:hover": {
        color: theme.colors.primary,
      },
    },
  });

export default function NavigationBar({handleSignOut}) {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const activeRoute = useLocation().pathname;
  const history = useHistory();
  const {t} = useTranslation();
  const hideNavbar = useSelector((state) => state.defaultReducer.hideNavbar);
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const [anchorEl, setAnchorEl] = useState(null);

  const handleRouteClick = (route) => {
    if (!activeRoute.includes(route)) {
      history.push(route);
    }
  };

  if (hideNavbar) return null;

  const avatar = (
    <ButtonIcon
      icon={
        <ProfilePicture
          image={user_profile.profile_picture}
          name={user_profile.name}
          sx={
            !!user_profile.profile_picture
              ? {
                  backgroundColor: theme.colors.white,
                  width: isMobile ? "44px" : "40px",
                  height: isMobile ? "44px" : "40px",
                }
              : {
                  width: isMobile ? "44px" : "40px",
                  height: isMobile ? "44px" : "40px",
                }
          }
        />
      }
      sx={{
        backgroundColor: "transparent",
        padding: 0,
        boxShadow: "none",
        "&:hover": {backgroundColor: "transparent"},
      }}
      onClick={(e) => setAnchorEl(e.currentTarget)}
    />
  );

  const menuItems = [
    !isMobile && user_profile.scopes?.accounts !== "hide"
      ? {
          label: t("navbar.user-avatar.menu.my-profile"),
          icon: ICONS.user,
          onClick: () => {
            setAnchorEl(null);
            history.push(`/admin/settings/users/${user_profile.enso_user_id}`);
          },
        }
      : null,
    {
      label: t("navbar.user-avatar.menu.logout"),
      icon: ICONS.logout,
      onClick: () => {
        setAnchorEl(null);
        handleSignOut();
      },
    },
  ];

  const contextMenu = (
    <ContextMenu
      menuItems={menuItems}
      open={!!anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorEl={anchorEl}
      modalProps={{
        anchorOrigin: {
          vertical: "top",
          horizontal: isMobile ? "end" : "start",
        },
        modalPosition: {
          vertical: "bottom",
          horizontal: isMobile ? "end" : "start",
        },
      }}
    />
  );

  if (isMobile) {
    return (
      <>
        {contextMenu}
        <FlexContainer
          alignItems="flex-start"
          justifyContent="space-between"
          gap={theme.spacing(2)}
          sx={{
            backgroundColor: theme.colors.gray100,
            minHeight: "90px",
            maxHeight: "90px",
            width: "100%",
            padding: theme.spacing(2.5, 8, 0),
          }}
        >
          {mobileRoutes.map((r) => (
            <ButtonIcon
              key={r.id}
              icon={
                <SvgIcon
                  key={r.id}
                  component={r.icon}
                  className={`${classes.icon} ${activeRoute.includes(r.route) ? classes.activeIcon : ""}`}
                  sx={{
                    width: "28px",
                    height: "28px",
                  }}
                />
              }
              onClick={() => handleRouteClick(r.route)}
              className={classes.mobileBtnIcon}
            />
          ))}
          {avatar}
        </FlexContainer>
      </>
    );
  }

  return (
    <>
      {contextMenu}
      <FlexContainer
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          backgroundColor: theme.colors.gray100,
          minWidth: "72px",
          maxWidth: "72px",
          height: "100%",
          padding: theme.spacing(5, 0, 4),
        }}
      >
        {/* TOP ICONS*/}
        <FlexContainer
          flexDirection="column"
          alignItems="center"
          gap={theme.spacing(2)}
        >
          <SvgIcon
            component={ICONS.enso}
            sx={{
              color: theme.colors.primary,
              width: "28px",
              height: "28px",
              marginBottom: theme.spacing(3.5),
            }}
          />
          {routes.map((r) => (
            <ButtonIcon
              key={r.id}
              icon={
                <Tooltip
                  sx={{tooltip: {maxWidth: "72px", textWrap: "auto"}}}
                  tooltipContent={
                    <Text variant="textXS" weight="semibold" color="white">
                      {r.id}
                    </Text>
                  }
                  colors={{bg: "gray900", text: "white"}}
                >
                  <SvgIcon
                    key={r.id}
                    component={r.icon}
                    className={`${classes.icon} ${activeRoute.includes(r.route) ? classes.activeIcon : ""}`}
                    sx={{
                      marginLeft: r.id === "CRM" ? "1px" : 0,
                      width: "20px",
                      height: "20px",
                    }}
                  />
                </Tooltip>
              }
              onClick={() => handleRouteClick(r.route)}
              className={classes.btnIcon}
            />
          ))}
        </FlexContainer>

        {/* BOTTOM ICONS*/}
        <FlexContainer
          flexDirection="column"
          alignItems="center"
          gap={theme.spacing(6)}
        >
          {avatar}
        </FlexContainer>
      </FlexContainer>
    </>
  );
}
